import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Button } from '../../components/admin/shared/Button';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import ProjectEditor from '../../components/admin/ProjectEditor';
import { Plus, Edit, Trash2, Star } from 'lucide-react';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const { toasts, addToast } = useToast();

  const fetchProjects = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*, categories(*)')
        .order('order', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      addToast('Error loading projects', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const fetchCategories = useCallback(async () => {
    try {
      const { error } = await supabase
        .from('categories')
        .select('*')
        .order('order');

      if (error) throw error;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, [fetchProjects, fetchCategories]);

  const handleDelete = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
      addToast('Project deleted successfully', 'success');
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      addToast('Error deleting project', 'error');
    }
  };

  const handleAdd = () => {
    setEditingProject(null);
    setShowEditor(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowEditor(true);
  };

  const handleSave = () => {
    setShowEditor(false);
    setEditingProject(null);
    fetchProjects();
  };

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Projects</h1>
        <Button
          variant="primary"
          onClick={handleAdd}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Add New Project
        </Button>
      </div>

      {loading ? (
        <div className="text-white">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="bg-slate-800 rounded-lg p-12 text-center border border-slate-700">
          <p className="text-slate-400 mb-4">No projects yet. Add your first project!</p>
          <Button variant="primary" onClick={handleAdd}>
            Add Project
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:bg-slate-750 transition-colors"
            >
              <div className="flex gap-4">
                {project.image && (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-white">{project.title}</h3>
                    {project.featured && (
                      <Star size={20} className="text-yellow-500 fill-yellow-500" />
                    )}
                  </div>
                  {project.categories && project.categories.name && (
                    <span className="inline-block px-2 py-1 bg-violet-600/20 text-violet-400 rounded text-xs mb-2">
                      {project.categories.name}
                    </span>
                  )}
                  <p className="text-slate-400 text-sm mb-2 line-clamp-2">
                    {project.description}
                  </p>
                  {project.technologies && project.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {project.technologies.slice(0, 5).map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(project)}
                      className="flex items-center gap-1"
                    >
                      <Edit size={16} />
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(project.id)}
                      className="flex items-center gap-1"
                    >
                      <Trash2 size={16} />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <ProjectEditor
          project={editingProject}
          onClose={() => {
            setShowEditor(false);
            setEditingProject(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Projects;

